window.theme.getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

window.theme.setCookie = (name, value, days) => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

window.theme.getProductCardHTML = async (template, productIds) => {
  return (
    await Promise.all(
      productIds.map(async (id) => {
        try {
          const response = await fetch(`/products/${id}?view=${template}`, {
            headers: { Accept: "text/html" },
          });
          if (!response.ok) return null;

          const text = await response.text();
          let html = new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector(".product-card")?.outerHTML ?? null;

          if (!html) return null;

          // FIX 1: Strip loading="lazy" and replace with loading="eager" for search results
          // This ensures images display immediately instead of waiting for scroll
          html = html
            .replace(/loading="lazy"/g, 'loading="eager"')
            .replace(/loading=['"]lazy['"]/g, 'loading="eager"');

          // FIX 2: Remove Swiper carousel x-data to prevent init conflicts
          // The carousel x-data spans multiple lines in the HTML, so we need a more flexible regex
          // Simply strip out the entire x-data attribute before class="swiper"
          html = html.replace(/x-data="[^"]*"\s+class="swiper"/g, 'class="swiper"');
          
          // Additional safeguard: if the above didn't work, try removing x-data attributes more broadly
          // This catches cases where quotes might be mismatched or formatted differently
          html = html.replace(/\s+x-data[^>]*?(?=\s+class=)/g, '');

          // FIX 4: Remove the Swiper progress-bar binding. It reads `currentProgress`,
          // a variable that only exists inside the x-data FIX 2 just stripped, so left
          // in place it throws "ReferenceError: currentProgress is not defined" on
          // every injected card, every time Alpine tries to evaluate it.
          html = html.replace(/\s*:style="[^"]*currentProgress[^"]*"/g, '');

          // FIX 3: Remove @click.prevent from quick-add and other buttons - but ONLY for
          // search results, where cards should just navigate to product. Recently-viewed
          // and recommendations both rely on @click.prevent for their quick-add button
          // ($store.cart.add / $store.global_size_selector.show), so stripping it there
          // silently breaks quick add with no console error at all.
          if (template === 'search') {
            html = html.replace(/@click\.prevent[^>]*/g, '');
          }

          return html;
        } catch (error) {
          console.warn(`Failed to fetch product ${id}:`, error);
          return null;
        }
      }),
    )
  ).filter(Boolean);
};

window.theme.getTagalysRecommendations = async ({
  recommendationId,
  productIds,
  count,
  template,
}) =>
  new Promise((resolve, reject) => {
    if (
      !window.Tagalys ||
      !window.Tagalys.APIConnector ||
      !window.Tagalys.APIConnector.Recommendations ||
      typeof window.Tagalys.APIConnector.Recommendations.call !== "function"
    ) {
      reject(
        new Error(
          "Tagalys recommendations were unavailable when requested",
        ),
      );
      return;
    }

    try {
      window.Tagalys.APIConnector.Recommendations.call({
        params: {
          recommendationId,
          count,
          productIds,
        },
        onSuccess: async ({ title, products }) => {
          try {
            const productsHTML = await window.theme.getProductCardHTML(
              template,
              products.map(({ handle }) => handle),
            );

            resolve({ title, products: productsHTML });
          } catch (error) {
            reject(error);
          }
        },
        onFailure: (error) => {
          reject(
            error instanceof Error
              ? error
              : new Error(
                  `Tagalys recommendation request failed: ${String(error)}`,
                ),
          );
        },
      });
    } catch (error) {
      reject(error);
    }
  });

window.theme.addToWatchlist = ({
  productId: empi,
  variantId: epi,
  productUrl: du,
  imageUrl: iu,
  price: pr,
}) => {
  window._swat.addToWatchList(null, {
    epi,
    empi,
    du,
    iu,
    pr,
  });
};

const createToggleStore = (name) => ({
  current: null,
  onClose: {},
  reopenCart: false,

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const openName = urlParams.get(`open_${name}`);

    if (openName) {
      this.current = openName;
    }
  },

  open(name) {
    const current = this.current;
    if (current) this.close();
    if (current === "cart") {
      this.reopenCart = true;
    }

    this.current = name;
  },

  close() {
    if (this.onClose[this.current]) {
      const close = this.onClose[this.current];
      setTimeout(() => {
        close();
      }, 300);
    }

    if (this.reopenCart) {
      this.current = "cart";
      this.reopenCart = false;
      return;
    }

    this.current = null;
  },
});

const createSizeSelectorStore = (methods) => ({
  error: null,
  selected: null,

  select(variant) {
    this.error = null;
    this.selected = variant;
  },

  async addToCart() {
    this.error = null;

    const json = await Alpine.store("cart").add(this.selected.id, false);
    if (json.status) {
      this.error = json.message;
      return;
    }

    Alpine.store("drawer").open("cart");
  },

  ...methods,
});

const initGorgiasChat = () => {
  const callback = () => {
    window.GorgiasChat.on("widget:opened", () =>
      document.body.classList.add("gorgias-chat-open"),
    );

    window.GorgiasChat.on("widget:closed", () => {
      document.body.classList.remove("gorgias-chat-open");
    });
  };

  if (window.GorgiasChat) {
    callback();
  } else {
    window.addEventListener("gorgias-widget-loaded", () => {
      callback();
    });
  }
};

const getTagalysDiagnostics = () => {
  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;

  const matchingScripts = Array.from(
    document.querySelectorAll("script[src]"),
  )
    .map((script) => script.src)
    .filter((source) => /tagalys|api-connector/i.test(source))
    .map((source) => source.split("?")[0]);

  const resourceEntries =
    typeof performance !== "undefined" &&
    typeof performance.getEntriesByType === "function"
      ? performance
          .getEntriesByType("resource")
          .filter((entry) =>
            /tagalys|api-connector/i.test(entry.name),
          )
          .slice(-5)
          .map((entry) => ({
            name: entry.name.split("?")[0],
            initiator_type: entry.initiatorType,
            duration_ms: Math.round(entry.duration),
            transfer_size: entry.transferSize,
            encoded_body_size: entry.encodedBodySize,
            decoded_body_size: entry.decodedBodySize,
          }))
      : [];

  return {
    tagalys_global_available: Boolean(window.Tagalys),
    set_configuration_available: Boolean(
      window.Tagalys &&
        typeof window.Tagalys.setConfiguration === "function"
    ),
    set_platform_configuration_available: Boolean(
      window.Tagalys &&
        typeof window.Tagalys.setPlatformConfiguration === "function"
    ),
    api_connector_available: Boolean(
      window.Tagalys && window.Tagalys.APIConnector
    ),
    online: navigator.onLine,
    document_ready_state: document.readyState,
    visibility_state: document.visibilityState,
    connection_effective_type:
      connection && connection.effectiveType
        ? connection.effectiveType
        : null,
    connection_downlink:
      connection && typeof connection.downlink === "number"
        ? connection.downlink
        : null,
    connection_save_data:
      connection && typeof connection.saveData === "boolean"
        ? connection.saveData
        : null,
    matching_script_count: matchingScripts.length,
    matching_scripts: matchingScripts,
    resource_entries: resourceEntries,
  };
};

const addTagalysBreadcrumb = (stage, level = "info") => {
  window.__emelloTagalysStage = stage;

  if (
    window.Sentry &&
    typeof window.Sentry.addBreadcrumb === "function"
  ) {
    window.Sentry.addBreadcrumb({
      category: "emello.tagalys",
      message: stage,
      level,
      data: {
        online: navigator.onLine,
        ready_state: document.readyState,
        visibility_state: document.visibilityState,
      },
    });
  }
};

const getEmelloEnvironmentContext = () => {
  const ua = navigator.userAgent || "";

  let inAppPlatform = "none";
  if (/Instagram/i.test(ua)) {
    inAppPlatform = "instagram";
  } else if (/FBAN|FBAV|FB_IAB/i.test(ua)) {
    inAppPlatform = "facebook";
  } else if (/musical_ly|TikTok|BytedanceWebview/i.test(ua)) {
    inAppPlatform = "tiktok";
  } else if (/\bwv\b|WebView/i.test(ua)) {
    inAppPlatform = "other";
  }

  const path = window.location.pathname;
  let pageType = "other";
  if (path.indexOf("/products/") !== -1) {
    pageType = "product";
  } else if (path.indexOf("/collections/") !== -1) {
    pageType = "collection";
  } else if (path.indexOf("/search") !== -1) {
    pageType = "search";
  } else if (path.indexOf("/cart") !== -1) {
    pageType = "cart";
  } else if (path === "/") {
    pageType = "home";
  }

  const connection = navigator.connection || {};

  return {
    in_app_browser: inAppPlatform !== "none",
    in_app_platform: inAppPlatform,
    page_type: pageType,
    online_status: navigator.onLine,
    visibility_state: document.visibilityState,
    document_ready_state: document.readyState,
    connection_effective_type:
      connection.effectiveType || "unavailable",
    connection_save_data:
      typeof connection.saveData === "boolean"
        ? connection.saveData
        : "unavailable",
    milliseconds_since_page_load: Math.round(performance.now()),
    sdk_present: Boolean(window.Tagalys),
    api_connector_present: Boolean(
      window.Tagalys && window.Tagalys.APIConnector,
    ),
  };
};

const captureTagalysFailure = (stage, error, feature, options) => {
  const capturedError =
    error instanceof Error ? error : new Error(String(error));
  const settings = options || {};

  if (
    window.Sentry &&
    typeof window.Sentry.withScope === "function" &&
    typeof window.Sentry.captureException === "function"
  ) {
    window.Sentry.withScope((scope) => {
      const environment = getEmelloEnvironmentContext();

      let classification = settings.classification || "unknown";
      if (environment.online_status === false) {
        classification = "offline";
      }

      scope.setTag("feature", feature || "tagalys_initialisation");
      scope.setTag("integration", "tagalys");
      scope.setTag("tagalys_stage", stage);
      scope.setTag("storefront_continued", "true");
      scope.setTag("error_classification", classification);
      scope.setTag(
        "storefront_impact",
        settings.storefrontImpact || "unknown",
      );
      scope.setTag(
        "in_app_browser",
        environment.in_app_browser ? "true" : "false",
      );
      scope.setTag("in_app_platform", environment.in_app_platform);
      scope.setTag("page_type", environment.page_type);
      scope.setTag(
        "online_status",
        environment.online_status ? "true" : "false",
      );
      scope.setTag("visibility_state", environment.visibility_state);

      scope.setContext("emello_environment", environment);
      scope.setContext(
        "tagalys_diagnostics",
        getTagalysDiagnostics(),
      );

      window.Sentry.captureException(capturedError);
    });
  }
};

document.addEventListener("alpine:init", () => {
  window.__emelloTagalysReady = false;
  window.__emelloTagalysStage = "not_started";

  let tagalysStage = "availability_check";

  try {
    addTagalysBreadcrumb("tagalys_availability_check_started");

    if (
      !window.Tagalys ||
      typeof window.Tagalys.setConfiguration !== "function" ||
      typeof window.Tagalys.setPlatformConfiguration !== "function"
    ) {
      throw new Error(
        "Tagalys API Connector was unavailable during Alpine initialisation",
      );
    }

    addTagalysBreadcrumb("tagalys_availability_check_completed");

    tagalysStage = "set_configuration";
    addTagalysBreadcrumb("tagalys_set_configuration_started");

    window.Tagalys.setConfiguration({
      api: {
        serverUrl: "https://api-r4.tagalys.com",
        apiKey: "tgls_v2_storefront_a610943c9248f30bf95c1321097c998e",
      },
      currency: {
        fractionalDigits: 2,
        forceFractionalDigits: true,
      },
      analyticsStorageConsentProvided: function () {
        return true;
      },
      track: true,
    });

    addTagalysBreadcrumb("tagalys_set_configuration_completed");

    tagalysStage = "set_platform_configuration";
    addTagalysBreadcrumb(
      "tagalys_set_platform_configuration_started",
    );

    window.Tagalys.setPlatformConfiguration({
      platform: "Shopify",
      countryCode: window.theme.settings.country,
      baseCountryCode: "GB",
      useStorefrontAPIForSecondaryMarkets: true,
      waitForStorefrontAPI: true,
      storefrontAPI: {
        accessToken: "8df1d820b782bf0101fe5a7bba5d5687",
        myShopifyDomain: "yapgdx-1m.myshopify.com",
      },
      metafields: {
        products: [],
      },
    });

    addTagalysBreadcrumb(
      "tagalys_set_platform_configuration_completed",
    );

    tagalysStage = "complete";
    window.__emelloTagalysReady = true;
    addTagalysBreadcrumb("tagalys_initialisation_completed");
  } catch (error) {
    window.__emelloTagalysReady = false;
    addTagalysBreadcrumb(`tagalys_failure_${tagalysStage}`, "error");

    console.error(
      "Tagalys initialisation failed. Core storefront functionality will continue.",
      error,
    );

    captureTagalysFailure(
      tagalysStage,
      error,
      "tagalys_initialisation",
      {
        classification:
          tagalysStage === "availability_check"
            ? "sdk_not_loaded"
            : "configuration_failed",
        storefrontImpact: "degraded",
      },
    );
  }

  try {
    initGorgiasChat();
  } catch (error) {
    console.error(
      "Gorgias initialisation failed. Core storefront functionality will continue.",
      error,
    );

    if (
      window.Sentry &&
      typeof window.Sentry.withScope === "function" &&
      typeof window.Sentry.captureException === "function"
    ) {
      window.Sentry.withScope((scope) => {
        scope.setTag("feature", "gorgias_initialisation");
        scope.setTag("integration", "gorgias");
        scope.setTag("storefront_continued", "true");
        window.Sentry.captureException(error);
      });
    }
  }

  Alpine.store("drawer", createToggleStore("drawer"));
  Alpine.store("modal", createToggleStore("modal"));

  Alpine.store("image_view", {
    current:
      window.theme.getCookie("image_view") ||
      window.theme.settings.defaultImageView,

    init() {
      const urlParams = new URLSearchParams(window.location.search);
      const imageView = urlParams.get(`image_view`);

      if (imageView && ["product", "model", "plus_size"].includes(imageView)) {
        this.current = imageView;
      }
    },

    set(view) {
      this.current = view;
      window.theme.setCookie("image_view", view, 30);
    },
  });

  Alpine.store(
    "global_size_selector",
    createSizeSelectorStore({
      product: null,

      show(product) {
        if (!product?.variants?.length) return;
        this.product = product;
        Alpine.store("drawer").open("global-size-selector");
      },

      clear() {
        this.loading = false;
        this.error = null;
        this.product = null;
        this.selected = null;
      },
    }),
  );

  Alpine.store(
    "pdp_size_selector",
    createSizeSelectorStore({
      select(variant) {
        this.error = null;
        this.selected = variant;
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}?variant=${variant.id}`,
        );
      },

      init() {
        const urlParams = new URLSearchParams(window.location.search);
        const variantId = urlParams.get("variant");
        if (!variantId) return;

        const variant = window.theme.product_variants.find(
          (v) => v.id == parseInt(variantId, 10),
        );

        if (variant) this.select(variant);
      },
    }),
  );

  Alpine.store("size_guide", {
    product: null,

    open(product) {
      this.product = product;
      Alpine.store("modal").open("size-guide");
    },
  });

  Alpine.store("cart", {
    loading: false,
    object: null,
    recommendations: null,

    async fetchCart() {
      const response = await fetch(window.theme.routes.cart, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          Accept: "text/html",
        },
      });

      const json = await response.json();
      this.object = json;
    },

    async updateRecommendations() {
      if (!this.object.items?.length || !window.theme.settings.cart_recommendations_id) {
        this.recommendations = null;
        return;
      }

      try {
        this.recommendations = await window.theme.getTagalysRecommendations({
          recommendationId: window.theme.settings.cart_recommendations_id,
          productIds: this.object.items.map(({ product_id }) => product_id),
          count: 4,
          template: "cart-recommendations",
        });
      } catch (ex) {
        this.recommendations = null;
      }
    },

    async updateDrawer(drawer) {
      await this.fetchCart();
      await this.updateRecommendations();

      const newContent = new DOMParser()
        .parseFromString(drawer, "text/html")
        .querySelector("#cart-drawer");

      if (newContent)
        document.querySelector("#cart-drawer").innerHTML = newContent.innerHTML;
    },

    async add(variantId, openDrawer = true) {
      this.loading = true;

      if (openDrawer) {
        Alpine.store("drawer").open("cart");
      }

      // Include selling plan data for preorder variants
      const form = document.querySelector("#main-pdp-form");
      const sellingPlanInput = form?.querySelector(".prebuy-selling-plan");
      const locationInput = form?.querySelector(".prebuy-location-property");

      const cartData = {
        id: variantId,
        quantity: 1,
        sections: "cart-drawer",
      };

      // Add selling plan if available
      if (
        sellingPlanInput &&
        !sellingPlanInput.disabled &&
        sellingPlanInput.value
      ) {
        cartData.selling_plan = sellingPlanInput.value;
      }

      // Add location property if available
      if (locationInput && !locationInput.disabled && locationInput.value) {
        cartData.properties = { _locationId: locationInput.value };
      }

      const body = JSON.stringify(cartData);
      const response = await fetch(window.theme.routes.cartAdd, {
        method: "POST",
        body,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
      });

      const json = await response.json();
      if (json.sections) {
        await this.updateDrawer(json.sections["cart-drawer"]);
      }

      Alpine.store("modal").close();
      this.loading = false;

      return json;
    },

    async remove(variantId) {
      this.loading = true;

      const body = JSON.stringify({
        updates: {
          [variantId]: 0,
        },
        sections: "cart-drawer",
      });

      const response = await fetch(window.theme.routes.cartUpdate, {
        method: "POST",
        body,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Content-Type": "application/json",
          Accept: "application/javascript",
        },
      });

      const json = await response.json();
      if (json.sections) {
        await this.updateDrawer(json.sections["cart-drawer"]);
      }

      this.loading = false;
      return json;
    },

    async init() {
      await this.fetchCart();
      await this.updateRecommendations();
    },
  });

  Alpine.store("search", {
    visible: false,
    loading: true,
    query: "",
    tagalysSearch: null,
    recommendations: null,
    suggestions: null,

    toggle() {
      this.visible = !this.visible;
    },

    async updateSuggestions() {
      if (!this.query) {
        this.suggestions = null;
        this.loading = false;
        return;
      }

      if (
        !this.tagalysSearch ||
        !this.tagalysSearch.helpers ||
        typeof this.tagalysSearch.helpers.updateQuery !== "function"
      ) {
        this.suggestions = null;
        this.loading = false;
        return;
      }

      this.loading = true;

      try {
        this.tagalysSearch.helpers.updateQuery(this.query);
      } catch (error) {
        this.loading = false;
        this.suggestions = null;
        captureTagalysFailure(
          "search_suggestion_update",
          error,
          "tagalys_search",
          {
            classification: "search_network_failure",
            storefrontImpact: "degraded",
          },
        );
      }
    },

    async init() {
      try {
        if (
          !window.__emelloTagalysReady ||
          !window.Tagalys ||
          !window.Tagalys.APIConnector
        ) {
          this.loading = false;
          this.recommendations = null;
          this.suggestions = null;
          return;
        }

        if (window.theme.settings.search_recommendations_id) {
          try {
            const { products: recommendations } =
              await window.theme.getTagalysRecommendations({
                recommendationId:
                  window.theme.settings.search_recommendations_id,
                count: 4,
                template: "search",
              });

            this.recommendations = recommendations;
          } catch (error) {
            this.recommendations = null;
            captureTagalysFailure(
              "search_recommendations",
              error,
              "tagalys_search",
              {
                classification: "search_network_failure",
                storefrontImpact: "degraded",
              },
            );
          }
        } else {
          this.recommendations = null;
        }

        if (
          !window.Tagalys.APIConnector.SearchSuggestions ||
          typeof window.Tagalys.APIConnector.SearchSuggestions.new !==
            "function"
        ) {
          throw new Error(
            "Tagalys SearchSuggestions API was unavailable",
          );
        }

        this.tagalysSearch =
          window.Tagalys.APIConnector.SearchSuggestions.new({
            params: {
              sections: {
                products: { count: 4 },
              },
            },
            onSuccess: async function ({
              sections: {
                products: { items: products },
              },
            }) {
              if (!products?.length) {
                Alpine.store("search").suggestions = [];
                Alpine.store("search").loading = false;
                return;
              }

              try {
                const productsHTML =
                  await window.theme.getProductCardHTML(
                    "search",
                    products.map(({ handle }) => handle),
                  );

                Alpine.store("search").suggestions = productsHTML;
              } catch (error) {
                Alpine.store("search").suggestions = null;
                captureTagalysFailure(
                  "search_product_cards",
                  error,
                  "tagalys_search",
                  {
                    classification: "search_network_failure",
                    storefrontImpact: "degraded",
                  },
                );
              } finally {
                Alpine.store("search").loading = false;
              }
            },
            onFailure: function (error) {
              Alpine.store("search").suggestions = null;
              Alpine.store("search").loading = false;
              captureTagalysFailure(
                "search_suggestions_request",
                error,
                "tagalys_search",
                {
                  classification: "search_network_failure",
                  storefrontImpact: "degraded",
                },
              );
            },
          });

        this.loading = false;

        Alpine.effect(() => {
          const value = this.query;
          void value;
          this.updateSuggestions();
        });
      } catch (error) {
        this.loading = false;
        this.recommendations = null;
        this.suggestions = null;
        this.tagalysSearch = null;
        captureTagalysFailure(
          "search_initialisation",
          error,
          "tagalys_search",
          {
            classification: "api_connector_not_ready",
            storefrontImpact: "degraded",
          },
        );
      }
    },
  });

  Alpine.store("localization", {
    shopify_localization: window.theme.settings.country,
    user_localization: null,

    async getCountryFromIP() {
      try {
        const response = await fetch("https://ipapi.co/country_code/");
        const countryCode = await response.text();
        return countryCode.trim();
      } catch (error) {
        console.error("Geolocation failed, using default");
        return "GB";
      }
    },

    async init() {
      const url = new URL(window.location.href);
      const countryParam = url.searchParams.get("country");

      if (countryParam) {
        window.theme.setCookie("localization_redirected", countryParam, 7);
        return;
      }

      const savedCountry = window.theme.getCookie("localization_redirected");
      if (savedCountry) {
        return;
      }

      this.user_localization = await this.getCountryFromIP();

      // Cache the result for 7 days regardless of outcome. Previously this
      // cookie was only set on the redirect branch below, so any visitor
      // whose detected country already matched the shop's default (GB is
      // both Emello's default market and its majority audience) never got
      // cached - meaning ipapi.co was called again on every single page
      // view, for most visitors, forever. That's what was driving the
      // repeated 429 (Too Many Requests) / CORS console errors.
      window.theme.setCookie(
        "localization_redirected",
        this.user_localization,
        7,
      );

      if (this.user_localization !== this.shopify_localization) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("country", this.user_localization);
        window.location.replace(newUrl.toString());
      }
    },
  });
});